// Authentication Context for managing user session state
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import firebaseAuth from '../services/FirebaseAuth.js';
import firebaseDatabase from '../services/FirebaseDatabase.js';
import { useGameSession } from '../hooks/useGameSession.js';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Game session management
  const gameSession = useGameSession();

  // Initialize authentication state listener and handle redirect results
  useEffect(() => {
    let unsubscribe;

    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing authentication and checking for redirect results...');
        console.log('🔍 Current URL:', window.location.href);

        // CRITICAL FIX: Check for redirect result BEFORE setting up auth state listener
        // This prevents the race condition where onAuthStateChanged fires before we can process the redirect
        const redirectResult = await firebaseAuth.handleRedirectResult();

        console.log('🔍 Redirect result:', redirectResult);

        if (redirectResult.hasRedirect) {
          console.log('📱 Redirect result detected:', {
            success: redirectResult.success,
            hasUser: !!redirectResult.user,
            errorType: redirectResult.errorType,
            userEmail: redirectResult.user?.email,
            userDisplayName: redirectResult.user?.displayName
          });

          if (redirectResult.success && redirectResult.user) {
            console.log('✅ Successful redirect sign-in for user:', redirectResult.user.email);
            console.log('✅ User details:', {
              uid: redirectResult.user.uid,
              email: redirectResult.user.email,
              displayName: redirectResult.user.displayName,
              isNewUser: redirectResult.isNewUser
            });

            // Handle successful redirect sign-in
            if (redirectResult.isNewUser) {
              console.log('👤 Creating player document for new Google user');

              // Create initial player document for new Google users
              const playerResult = await firebaseDatabase.createOrUpdatePlayer(redirectResult.user.uid, {
                playername: redirectResult.user.displayName || '',
                metersSkatedRecord: 0,
                topWords: []
              });

              if (!playerResult.success) {
                console.error('❌ Failed to create player document:', playerResult.error);
                setAuthError(playerResult.error);
              } else {
                console.log('✅ Player document created successfully');
              }
            } else {
              console.log('👤 Existing user - player document should already exist');
            }
          } else if (!redirectResult.success) {
            // Handle redirect error with enhanced logging
            console.error('❌ Redirect authentication failed:', {
              error: redirectResult.error,
              errorType: redirectResult.errorType,
              originalError: redirectResult.originalError
            });

            // Provide more specific error messages for Safari
            let errorMessage = redirectResult.error;
            if (redirectResult.errorType === 'timeout') {
              errorMessage = 'Authentication timed out. This sometimes happens on Safari. Please try again.';
            }

            setAuthError(errorMessage);
          }
        } else {
          console.log('ℹ️ No redirect result found - normal page load');
        }
      } catch (error) {
        console.error('❌ Error during authentication initialization:', error);
        console.error('❌ Error stack:', error.stack);
        setAuthError('Authentication initialization failed. Please refresh the page and try again.');
      } finally {
        // CRITICAL FIX: Set up auth state listener AFTER redirect result is processed
        // This ensures we don't miss the redirect result due to race conditions
        console.log('🔐 Setting up auth state listener...');
        unsubscribe = firebaseAuth.onAuthStateChange(async (user) => {
          console.log('🔐 Firebase auth state changed:', {
            hasUser: !!user,
            uid: user?.uid,
            email: user?.email,
            displayName: user?.displayName,
            isAnonymous: user?.isAnonymous
          });

          setCurrentUser(user);
          setAuthError(null);

          if (user) {
            // Load player data when user signs in
            console.log('👤 User signed in, loading player data...');
            const result = await firebaseDatabase.getPlayerData(user.uid);
            if (result.success) {
              console.log('✅ Player data loaded:', {
                metersRecord: result.data.metersSkatedRecord,
                totalScore: result.data.totalScore,
                bestGameScore: result.data.bestGameScore
              });
              setPlayerData(result.data);
            } else {
              console.error('❌ Failed to load player data:', result.error);
              setAuthError(result.error);
            }
          } else {
            // Clear player data when user signs out
            console.log('👤 User signed out, clearing player data');
            setPlayerData(null);
          }

          setLoading(false);
        });
      }
    };

    // Initialize redirect handling
    console.log('🚀 AuthContext mounted - initializing auth...');
    initializeAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Authentication methods
  const register = async (email, password, displayName) => {
    setLoading(true);
    setAuthError(null);
    
    const result = await firebaseAuth.register(email, password, displayName);
    
    if (result.success) {
      // Create initial player document
      const playerResult = await firebaseDatabase.createOrUpdatePlayer(result.user.uid, {
        playername: displayName || '',
        metersSkatedRecord: 0,
        topWords: []
      });
      
      if (!playerResult.success) {
        setAuthError(playerResult.error);
      }
    } else {
      setAuthError(result.error);
    }
    
    setLoading(false);
    return result;
  };

  const login = async (email, password) => {
    setLoading(true);
    setAuthError(null);

    const result = await firebaseAuth.login(email, password);

    if (!result.success) {
      setAuthError(result.error);
    }

    setLoading(false);
    return result;
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    setAuthError(null);

    console.log('🔐 Starting Google sign-in from AuthContext...');

    const result = await firebaseAuth.signInWithGoogle();

    console.log('🔐 Google sign-in result:', {
      success: result.success,
      pending: result.pending,
      authMethod: result.authMethod,
      errorType: result.errorType
    });

    if (result.success) {
      if (result.pending) {
        console.log('📱 Redirect authentication initiated - page will reload');
        // For mobile redirect, don't set loading to false as page will reload
        return result;
      }

      console.log('✅ Popup authentication successful');

      // For popup success (desktop), handle new user creation
      if (result.isNewUser) {
        console.log('👤 Creating player document for new Google user (popup)');

        const playerResult = await firebaseDatabase.createOrUpdatePlayer(result.user.uid, {
          playername: result.user.displayName || '',
          metersSkatedRecord: 0,
          topWords: []
        });

        if (!playerResult.success) {
          console.error('❌ Failed to create player document (popup):', playerResult.error);
          setAuthError(playerResult.error);
        } else {
          console.log('✅ Player document created successfully (popup)');
        }
      }
    } else {
      console.error('❌ Google sign-in failed:', {
        error: result.error,
        errorType: result.errorType,
        originalError: result.originalError
      });

      // Enhanced error handling for Safari-specific issues
      let errorMessage = result.error;
      if (result.errorType === 'timeout') {
        errorMessage = 'Sign-in timed out. This sometimes happens on Safari. Please try again.';
      } else if (result.originalError === 'auth/popup-blocked' && result.errorType === 'firebase') {
        errorMessage = 'Pop-up was blocked. Please allow pop-ups for this site or try using Safari.';
      }

      setAuthError(errorMessage);
    }

    setLoading(false);
    return result;
  };

  const logout = async () => {
    setLoading(true);
    setAuthError(null);
    
    const result = await firebaseAuth.logout();
    
    if (!result.success) {
      setAuthError(result.error);
    }
    
    setLoading(false);
    return result;
  };

  // Player data methods
  const updateMetersRecord = async (metersSkated) => {
    if (!currentUser) return { success: false, error: 'User not authenticated' };
    
    const result = await firebaseDatabase.updateMetersRecord(currentUser.uid, metersSkated);
    
    if (result.success && result.isNewRecord) {
      // Update local player data
      setPlayerData(prev => ({
        ...prev,
        metersSkatedRecord: result.newRecord
      }));
    }
    
    return result;
  };

  const addTopWord = async (word, points) => {
    if (!currentUser) return { success: false, error: 'User not authenticated' };
    
    const result = await firebaseDatabase.addTopWord(currentUser.uid, word, points);
    
    if (result.success && result.madeTopTen) {
      // Refresh player data to get updated top words
      const playerResult = await firebaseDatabase.getPlayerData(currentUser.uid);
      if (playerResult.success) {
        setPlayerData(playerResult.data);
      }
    }
    
    return result;
  };

  const updatePlayerName = async (playername) => {
    if (!currentUser) return { success: false, error: 'User not authenticated' };

    const result = await firebaseDatabase.updatePlayerName(currentUser.uid, playername);

    if (result.success) {
      // Update local player data
      setPlayerData(prev => ({
        ...prev,
        playername: playername
      }));
    }

    return result;
  };

  // Session-based game methods (new implementation)

  /**
   * Initialize a new game session
   * Loads player data from Firebase and initializes session tracking
   * Only initializes once per session to prevent data loss during gameplay
   */
  const initializeGameSession = useCallback(async () => {
    try {
      console.log('🎮 initializeGameSession called');
      console.log('🔐 Current user state:', {
        hasCurrentUser: !!currentUser,
        uid: currentUser?.uid,
        email: currentUser?.email,
        displayName: currentUser?.displayName,
        isAnonymous: currentUser?.isAnonymous
      });

      if (currentUser) {
        // For authenticated users, fetch current player data
        console.log('🔍 Fetching player data from Firebase for uid:', currentUser.uid);
        const result = await firebaseDatabase.getPlayerData(currentUser.uid);
        if (result.success) {
          console.log('✅ Got player data from Firebase:', {
            metersRecord: result.data.metersSkatedRecord,
            totalMeters: result.data.totalMeters,
            totalScore: result.data.totalScore,
            bestGameScore: result.data.bestGameScore,
            topWordsCount: result.data.topWords?.length || 0
          });
          gameSession.initializeSession(result.data);
          return { success: true, playerData: result.data };
        } else {
          // Fallback to default data if fetch fails
          console.warn('⚠️ Failed to get player data from Firebase, using defaults:', result.error);
          gameSession.initializeSession();
          return { success: false, error: result.error };
        }
      } else {
        // For anonymous users, initialize with empty data
        console.log('👤 Anonymous user - initializing with default empty data');
        gameSession.initializeSession();
        return { success: true, playerData: null };
      }
    } catch (error) {
      console.error('❌ Error initializing game session:', error);
      gameSession.initializeSession();
      return { success: false, error: 'Failed to load player data' };
    }
  }, [currentUser, gameSession]);

  /**
   * Add a word to the current game session (local tracking only)
   */
  const addSessionWord = useCallback((word, points) => {
    gameSession.addSessionWord(word, points);
  }, [gameSession]);

  /**
   * Update meters in the current game session (local tracking only)
   */
  const updateSessionMeters = useCallback((meters) => {
    gameSession.updateSessionMeters(meters);
  }, [gameSession]);

  /**
   * Finalize game session and batch update Firebase at game over
   * Only updates fields that have actually changed
   */
  const finalizeGameSession = useCallback(async () => {
    try {
      if (!currentUser) {
        // For anonymous users, return session stats without saving
        const stats = gameSession.getSessionStats();
        return {
          success: true,
          isAnonymous: true,
          sessionStats: stats,
          message: 'Sign in to save your progress!'
        };
      }

      // Get updates that need to be saved to Firebase
      const updates = gameSession.getFirebaseUpdates();

      if (!updates) {
        // No updates needed
        return {
          success: true,
          hasUpdates: false,
          sessionStats: gameSession.getSessionStats()
        };
      }

      // Perform batch update to Firebase
      const result = await firebaseDatabase.batchUpdatePlayerData(currentUser.uid, updates);

      if (result.success && result.hasUpdates) {
        // Update global player stats first
        gameSession.updatePlayerStats(result.updateResults);

        // Update local player data with new values from Firebase batch update
        setPlayerData(prev => {
          const updatedData = { ...prev };

          // Update meters record if it was updated
          if (result.updateResults.metersRecord.updated) {
            updatedData.metersSkatedRecord = result.updateResults.metersRecord.newRecord;
          }

          // Update total meters if it was updated
          if (result.updateResults.totalMeters.updated) {
            updatedData.totalMeters = result.updateResults.totalMeters.newTotal;
          }

          // Update total score if it was updated
          if (result.updateResults.totalScore.updated) {
            updatedData.totalScore = result.updateResults.totalScore.newTotal;
          }

          // Update best game score if it was updated
          if (result.updateResults.bestGameScore.updated) {
            updatedData.bestGameScore = result.updateResults.bestGameScore.newBest;
          }

          // Update top words if they were updated
          if (result.updateResults.topWords.updated) {
            updatedData.topWords = updates.topWords;
          }

          console.log('✅ Local playerData updated with new cumulative values:', {
            totalMeters: updatedData.totalMeters,
            totalScore: updatedData.totalScore,
            bestGameScore: updatedData.bestGameScore,
            metersRecord: updatedData.metersSkatedRecord
          });

          return updatedData;
        });
      }

      return {
        success: result.success,
        hasUpdates: result.hasUpdates,
        updateResults: result.updateResults,
        sessionStats: gameSession.getSessionStats(),
        error: result.error
      };

    } catch (error) {
      console.error('❌ Error in finalizeGameSession:', error);
      console.error('❌ Error stack:', error.stack);
      return {
        success: false,
        error: 'Failed to save game progress',
        sessionStats: gameSession.getSessionStats()
      };
    }
  }, [currentUser, gameSession]);

  /**
   * Reset session for new game (keeps player data for reference)
   */
  const resetGameSession = useCallback(() => {
    gameSession.resetSession();
  }, [gameSession]);

  /**
   * Get current session statistics
   */
  const getSessionStats = useCallback(() => {
    return gameSession.getSessionStats();
  }, [gameSession]);

  const value = {
    currentUser,
    playerData,
    loading,
    authError,
    register,
    login,
    signInWithGoogle,
    logout,
    updateMetersRecord,
    addTopWord,
    updatePlayerName,
    isAuthenticated: !!currentUser,
    // Session-based methods (new implementation)
    initializeGameSession,
    addSessionWord,
    updateSessionMeters,
    finalizeGameSession,
    resetGameSession,
    getSessionStats
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
